import React, { useEffect, useState } from "react";
import { MessageSquarePlus, Star, Loader2, AlertCircle, CheckCircle, X } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { submitFeedback } from "../../services/feedbackService";

/**
 * Stella interattiva per il rating 1-5.
 */
function StarRating({ value, hoverValue, onChange, onHover, onHoverEnd, darkMode }) {
  const displayValue = hoverValue || value;
  return (
    <div className="flex items-center gap-1" role="radiogroup" aria-label="Valutazione da 1 a 5 stelle">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= displayValue;
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={`${n} stell${n === 1 ? "a" : "e"}`}
            onClick={() => onChange(n)}
            onMouseEnter={() => onHover(n)}
            onMouseLeave={onHoverEnd}
            onFocus={() => onHover(n)}
            onBlur={onHoverEnd}
            className="p-1 rounded-lg transition-transform hover:scale-110"
          >
            <Star
              className={`w-8 h-8 transition-colors ${
                filled
                  ? "fill-forfy-500 text-forfy-500"
                  : darkMode
                    ? "fill-transparent text-neutral-600"
                    : "fill-transparent text-neutral-300"
              }`}
            />
          </button>
        );
      })}
    </div>
  );
}

export default function FeedbackModal({ isOpen, onClose, darkMode = false }) {
  const { user } = useAuth();

  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [likes, setLikes] = useState("");
  const [dislikes, setDislikes] = useState("");
  const [suggestions, setSuggestions] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setRating(0);
      setHoverRating(0);
      setLikes("");
      setDislikes("");
      setSuggestions("");
      setLoading(false);
      setError(null);
      setSuccess(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!rating) {
      setError("Seleziona una valutazione da 1 a 5 stelle.");
      return;
    }

    setLoading(true);
    try {
      await submitFeedback({
        rating,
        likes: likes.trim(),
        dislikes: dislikes.trim(),
        suggestions: suggestions.trim(),
        userEmail: user?.email || "",
      });
      setSuccess(true);
    } catch (err) {
      console.error("submitFeedback error:", err);
      setError(err?.message || "Errore durante l'invio del feedback. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`w-full max-w-md rounded-2xl border shadow-xl max-h-[90vh] overflow-y-auto ${
          darkMode
            ? "bg-neutral-900 border-neutral-700 text-white"
            : "bg-white border-neutral-200 text-neutral-900"
        }`}
      >
        <div
          className={`flex items-center justify-between px-5 py-4 border-b sticky top-0 z-10 ${
            darkMode ? "bg-neutral-900 border-neutral-700" : "bg-white border-neutral-200"
          }`}
        >
          <div className="flex items-center gap-2">
            <MessageSquarePlus className={`w-5 h-5 ${darkMode ? "text-forfy-400" : "text-forfy-600"}`} />
            <h2 className="text-base font-semibold">Lascia un feedback</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            aria-label="Chiudi"
            className={`p-1 rounded-lg transition-colors ${
              darkMode ? "hover:bg-neutral-800 text-neutral-400" : "hover:bg-neutral-100 text-neutral-500"
            } disabled:opacity-50`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {success ? (
          <div className="p-5 space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle className={`w-5 h-5 shrink-0 mt-0.5 ${darkMode ? "text-emerald-400" : "text-emerald-600"}`} />
              <div>
                <h3 className="text-sm font-semibold">Grazie per il tuo feedback!</h3>
                <p className={`mt-1 text-sm ${darkMode ? "text-neutral-400" : "text-neutral-600"}`}>
                  Le tue segnalazioni ci aiutano a migliorare Forfy.
                </p>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-forfy-500 text-white text-sm hover:bg-forfy-600"
              >
                Chiudi
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <label className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>
                Quanto sei soddisfatto/a di Forfy? *
              </label>
              <div className="mt-2">
                <StarRating
                  value={rating}
                  hoverValue={hoverRating}
                  onChange={setRating}
                  onHover={setHoverRating}
                  onHoverEnd={() => setHoverRating(0)}
                  darkMode={darkMode}
                />
              </div>
            </div>

            <div>
              <label htmlFor="feedback-likes" className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>
                Cosa ti piace?
              </label>
              <textarea
                id="feedback-likes"
                rows={2}
                value={likes}
                onChange={(e) => setLikes(e.target.value)}
                disabled={loading}
                placeholder="Le funzionalità che apprezzi di più..."
                className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm resize-none ${
                  darkMode
                    ? "border-neutral-700 bg-neutral-800 text-white placeholder:text-neutral-600"
                    : "border-neutral-200 bg-white text-neutral-900 placeholder:text-neutral-400"
                } disabled:opacity-50`}
              />
            </div>

            <div>
              <label htmlFor="feedback-dislikes" className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>
                Cosa non ti piace?
              </label>
              <textarea
                id="feedback-dislikes"
                rows={2}
                value={dislikes}
                onChange={(e) => setDislikes(e.target.value)}
                disabled={loading}
                placeholder="Cosa ti ha creato difficoltà o non ti convince..."
                className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm resize-none ${
                  darkMode
                    ? "border-neutral-700 bg-neutral-800 text-white placeholder:text-neutral-600"
                    : "border-neutral-200 bg-white text-neutral-900 placeholder:text-neutral-400"
                } disabled:opacity-50`}
              />
            </div>

            <div>
              <label htmlFor="feedback-suggestions" className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>
                Cosa vorresti?
              </label>
              <textarea
                id="feedback-suggestions"
                rows={2}
                value={suggestions}
                onChange={(e) => setSuggestions(e.target.value)}
                disabled={loading}
                placeholder="Funzionalità o miglioramenti che vorresti vedere..."
                className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm resize-none ${
                  darkMode
                    ? "border-neutral-700 bg-neutral-800 text-white placeholder:text-neutral-600"
                    : "border-neutral-200 bg-white text-neutral-900 placeholder:text-neutral-400"
                } disabled:opacity-50`}
              />
            </div>

            {error && (
              <div
                className={`flex items-start gap-2 px-3 py-2 rounded-lg text-sm ${
                  darkMode ? "bg-red-900/20 text-red-400" : "bg-red-50 text-red-600"
                }`}
              >
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className={`px-4 py-2 rounded-lg border text-sm ${
                  darkMode
                    ? "border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
                    : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                } disabled:opacity-50`}
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={loading || !rating}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-forfy-500 text-white text-sm hover:bg-forfy-600 disabled:opacity-50"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? "Invio in corso..." : "Invia feedback"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
